import React, { useEffect, useState, useMemo } from "react";
import api from "../api/axios";

function TrialBalance() {
  const [tree, setTree] = useState([]);
  const [totals, setTotals] = useState({ debit: 0, credit: 0 });
  const [loading, setLoading] = useState(true);

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState(new Date().toISOString().slice(0, 10));

  const token = localStorage.getItem("accessToken");
  const branch_id = sessionStorage.getItem("selectedContext")
    ? JSON.parse(sessionStorage.getItem("selectedContext")).branch
    : 0;

  /* ===============================
     Fetch Trial Balance
  =============================== */
  const fetchTrialBalance = async () => {
    setLoading(true);
    try {
      const res = await api.get(
        `/reports/trial-balance?branch_id=${branch_id}&from_date=${fromDate}&to_date=${toDate}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setTree(res.data.tree || []);
      setTotals(res.data.totals || { debit: 0, credit: 0 });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTrialBalance();
  }, []);

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-GB");
  };

  const formatToday = () => {
    const today = new Date();
    return today.toLocaleDateString("en-GB");
  };

  const getReportTitle = () => {
    if (fromDate && toDate) {
      return `Trial Balance (${formatDate(fromDate)} To ${formatDate(toDate)})`;
    }
    if (!fromDate && toDate) {
      return `Trial Balance (Up to ${formatDate(toDate)})`;
    }
    if (fromDate && !toDate) {
      return `Trial Balance (From ${formatDate(fromDate)} To ${formatToday()})`;
    }
    return `Trial Balance (Up to ${formatToday()})`;
  };

  /* ===============================
     Calculate True Net Totals from Top-Level Roots
  =============================== */
  const netTotals = useMemo(() => {
    let netDebit = 0;
    let netCredit = 0;

    if (!Array.isArray(tree) || tree.length === 0) {
      return { debit: 0, credit: 0 };
    }

    tree.forEach((rootNode) => {
      const debit = Number(rootNode.debit) || 0;
      const credit = Number(rootNode.credit) || 0;
      const net = debit - credit;

      if (net > 0) {
        netDebit += net;
      } else if (net < 0) {
        netCredit += Math.abs(net);
      }
    });

    return { debit: netDebit, credit: netCredit };
  }, [tree]);

  /* ===============================
     Render Node Rows
  =============================== */
  const renderNode = (node, level = 1) => {
    const paddingLeft = `${level * 12}px`;

    /* ================= GROUP ================= */
    if (node.type === "group" && (node.debit !== 0 || node.credit !== 0)) {
      const netAmount = (Number(node.debit) || 0) - (Number(node.credit) || 0);
      return (
        <React.Fragment key={`group-${node.group_id}`}>
          <tr>
            <td style={{ paddingLeft }} className="fw-bold">
              {node.group_name}
            </td>
            <td></td>
            <td className="text-end fw-bold">
              {netAmount > 0 ? netAmount.toFixed(2) : ""}
            </td>
            <td className="text-end fw-bold">
              {netAmount < 0 ? Math.abs(netAmount).toFixed(2) : ""}
            </td>
          </tr>

          {node.children?.map((child) => renderNode(child, level + 1))}
        </React.Fragment>
      );
    }

    /* ================= LEDGER ================= */
    if (node.type === "ledger" && (node.debit !== 0 || node.credit !== 0)) {
      const netAmount = (Number(node.debit) || 0) - (Number(node.credit) || 0);
      return (
        <tr key={`ledger-${node.ledger_id}`}>
          <td style={{ paddingLeft }} className="text-muted">
            {node.ledger_code}
          </td>
          <td style={{ paddingLeft }}>{node.ledger_name}</td>
          <td className="text-end">
            {netAmount > 0 ? netAmount.toFixed(2) : ""}
          </td>
          <td className="text-end">
            {netAmount < 0 ? Math.abs(netAmount).toFixed(2) : ""}
          </td>
        </tr>
      );
    }

    return null;
  };

  return (
    <>
      {/* Page Header */}
      <div className="row mb-3 d-print-none">
        <div className="col-12 d-flex justify-content-between align-items-center">
          <h4 className="mb-0">Trial Balance</h4>
          <button className="btn btn-secondary" onClick={() => window.print()}>
            Print / Save PDF
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="row mb-3 d-print-none">
        <div className="col-md-3">
          <input
            type="date"
            className="form-control"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
          />
        </div>
        <div className="col-md-3">
          <input
            type="date"
            className="form-control"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
          />
        </div>
        <div className="col-md-2">
          <button className="btn btn-primary" onClick={fetchTrialBalance}>
            Show
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="row">
        <div className="col-xl-12">
          <div className="card border-0 shadow-none">
            <div className="card-body p-0">
              <h6 className="mb-4 text-center text-md-start fw-bold">
                {getReportTitle()}
              </h6>
              {loading ? (
                <p>Loading...</p>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm align-middle">
                    <thead className="table-light">
                      <tr>
                        <th>Account ID</th>
                        <th>Account Name</th>
                        <th className="text-end">Debit</th>
                        <th className="text-end">Credit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tree.length > 0 ? (
                        tree.map((node) => renderNode(node))
                      ) : (
                        <tr>
                          <td colSpan="4" className="text-center">
                            No data
                          </td>
                        </tr>
                      )}
                    </tbody>

                    {/* Footer rendered in Chrome Print */}
                    <tfoot className="table-light border-top">
                      <tr className="fw-bold">
                        <th colSpan="2">Trial Balance Total (Net)</th>
                        <th className="text-end text-primary">
                          {netTotals.debit.toFixed(2)}
                        </th>
                        <th className="text-end text-primary">
                          {netTotals.credit.toFixed(2)}
                        </th>
                      </tr>
                      <tr className="text-muted small d-none">
                        <th colSpan="2">Total Gross Movement</th>
                        <th className="text-end">
                          {(Number(totals.debit) || 0).toFixed(2)}
                        </th>
                        <th className="text-end">
                          {(Number(totals.credit) || 0).toFixed(2)}
                        </th>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default TrialBalance;